// src/services/productService.js
// 78차 62차: branch.isDeleted(지점별 비활성화) 지점의 테마는 flattenStoreThemes에서
// 제외 — store.isDeleted(매장 전체)와 별개로 지점 하나만 노출 내릴 수 있게 함.
// logContractChange에 branchIdx 태그 추가(어느 지점 변경인지 이력에 남김).
// ✅ 데이터 구조: stores/{storeId}/branches[]/themes[] 중첩 구조
// products 컬렉션은 별도로 없음 — stores에서 테마를 추출해 상품으로 변환
// 32차: softDeleteStore / restoreStore 추가 (isDeleted 플래그 방식)
// 55차: incrementViewCount 추가 — 테마 상세 조회수 수집 (분석 대시보드 퍼널용)
// 71차: updateThemeInStore의 ID 파싱을 split('_') → lastIndexOf('_') 방식으로 통일
//       (storeId에 '_'가 섞여 있으면 기존 방식은 잘못 파싱됨)
// 71차: parseProductId / getStoreDisplayName 신규 — 여러 파일에 흩어져 있던
//       ID 파싱, 매장 브랜드명 폴백 로직을 하나로 통합
// 72차: flattenStoreThemes에 isPaused(매장 일시 휴업) 필드 노출 추가
// 74차: flattenStoreThemes에 reservationUrl 필드 추가 — 이 필드가 없어서 예약 화면들이
//       항상 undefined로 읽고 외부 예약 URL 이동이 조용히 스킵되던 문제
// 74차: 영업용 데모 매장(stores.isDemo:true) 도입 → 고객 화면 필터링 → 롤백 → 재도입.
//       처음엔 모든 admin류 계정에게 보이는 넓은 조건(canPreviewDemoStores)이었다가
//       "데이터만 넣으면 될 줄 알았다"는 피드백으로 걷어냈는데, 다시 "고객에게 실제로
//       어떻게 보이는지 보여주려면 전용 데모 고객 계정 하나로만 보이게" 요청이 와서
//       stores.visibleToUid(단일 uid) 방식으로 재구현. plan(BASIC/PREMIUM 등 구독 등급)에
//       "DEMO" 등급을 끼워 넣는 방식은 의도적으로 피함 — plan은 매장이 쓸 수 있는 기능을
//       정하는 필드(planAtLeast 서열비교)라 노출 범위 제어와 섞으면 다른 플랜 게이트까지
//       꼬일 위험이 있음. 그래서 순수 노출 제어만 하는 별도 필드로 분리.
//       조건: !isDemo(일반 매장) 이거나, visibleToUid === 지금 보는 사람의 uid(전용 데모
//       계정) 이거나, 그 매장을 관리하는 매장관리자 본인(관리 화면 미리보기 유지)이면 보임.
// 75차: 데드코드 정리 — getPopularProducts/getProducts/getProductsByStoreIds 삭제
// (전부 호출부 없음 확인). 특히 getProductsByStoreIds는 CLAUDE.md에 매장관리자 화면이
// 쓰는 것처럼 기록돼 있었는데 실제로는 아무 데서도 안 부르고 있었음(문서-코드 불일치,
// 매장관리자 화면은 store.branches를 직접 읽는 방식이었음) — 문서도 같이 정정.
// 78차: 데드코드 정리 — updateThemeInStore 삭제(호출부 0곳 확인). 실제 테마 수정은
// StoreAdminScreen.js/SuperAdminStoresScreen.js가 전부 updateDoc(stores/{id}, {branches})를
// 직접 호출해서 처리 중이었음 — 71차에 이 함수의 ID 파싱 버그를 고친 이력이 있으나
// 애초에 아무도 호출하지 않는 함수였음.

import { collection, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { normalizeTimeStr } from '../utils/dateUtils';

// ─── productId("storeId_branchIdx_themeIdx") 파싱 ────────────
// storeId 자체에 '_'가 섞여 있을 수 있으므로 뒤에서부터 lastIndexOf('_')로 파싱.
// 71차: getProductById/incrementViewCount/updateThemeInStore가 각자 들고 있던
//   동일한 파싱 로직을 하나로 통합 (reviewService.js의 storeId 추출도 이 함수를 씀)
export const parseProductId = (id) => {
  if (!id) return null;
  const lastUnder = id.lastIndexOf('_');
  if (lastUnder < 0) return null;
  const ti = Number(id.slice(lastUnder + 1));
  const rest = id.slice(0, lastUnder);
  const midUnder = rest.lastIndexOf('_');
  if (midUnder < 0) return null;
  const bi = Number(rest.slice(midUnder + 1));
  const storeId = rest.slice(0, midUnder);
  if (!storeId || isNaN(bi) || isNaN(ti)) return null;
  return { storeId, branchIdx: bi, themeIdx: ti };
};

// ─── stores의 branches[].themes[]를 product 형태로 평탄화 ────
// 각 테마에 storeId, branchName, branchIdx, themeIdx를 붙여 반환
export const flattenStoreThemes = (stores) => {
  const products = [];
  for (const store of stores) {
    const branches = store.branches || [];
    branches.forEach((branch, bi) => {
      // 78차 62차: 지점별 비활성화 — 이 지점만 꺼두면 그 지점의 테마만 고객 화면에서
      // 숨긴다(store.isDeleted처럼 매장 전체를 내리는 것과는 별개, 다른 지점은 그대로 노출).
      if (branch.isDeleted === true) return;
      const themes = branch.themes || [];
      themes.forEach((theme, ti) => {
        // 78차 46차: 테마별 비활성화(theme.isDeleted) — 지점 삭제와 동일한 소프트 삭제
        // 방식. 지점/테마를 배열에서 실제로 splice하면 branchIdx/themeIdx가 밀려 기존
        // reservations/reviews/favorites/events의 productId(위치 기반)가 엉뚱한 지점·
        // 테마를 가리키게 되는 버그가 있었음(SuperAdminStoresScreen.js EditStore) —
        // 배열에서 빼지 않고 이 플래그만 세워 인덱스가 절대 안 밀리게 함.
        if (theme.isDeleted === true) return;
        products.push({
          // 식별자: "storeId_branchIdx_themeIdx"
          id: `${store.id}_${bi}_${ti}`,
          storeId: store.id,
          branchIdx: bi,
          themeIdx: ti,

          // 테마 필드
          name: theme.name || '',
          title: theme.name || '',           // 웹 호환
          description: theme.description || '',
          difficulty: theme.difficulty || '',
          duration: theme.duration || 60,
          genre: theme.genre || '',
          genres: theme.genre ? [theme.genre] : [],
          imageUrl: theme.imageUrl || '',
          thumbnailUrl: theme.imageUrl || '',
          maxPeople: theme.maxPeople || 0,
          minPeople: theme.minPeople || 1,
          // 80차: bookingFormat('room' 기본|'show') — 체험형 공연(정원제) 신규 지원.
          // capacity는 show일 때만 의미 있는 회차당 총 정원.
          bookingFormat: theme.bookingFormat || 'room',
          capacity: Number(theme.capacity) || 0,
          // 80차 후속: 체험형 전용 — 단독 대관('exclusive') vs 합석 일반 회차('shared', 기본)
          seatingType: theme.seatingType || 'shared',
          // 80차 후속: 체험형 전용 표시 필드 — activityLevel(난이도 대체), pricePerPerson
          // (1인당 가격, pricing 배열은 여기서 파생돼 저장되므로 예약 로직은 무변경),
          // notices/refundPolicy(유의사항/환불정책 문단).
          activityLevel: theme.activityLevel || 'normal',
          pricePerPerson: Number(theme.pricePerPerson) || 0,
          // 80차 후속: [버그 수정] isAdultOnly가 이 필드 리스트에서 누락돼 있어, 이 함수를
          // 거치는 모든 클라이언트 화면(카드 배지·클라이언트 소프트 게이트)에서 항상
          // undefined로 보이던 버그(fork로 발견) — 서버측 검증(functions/index.js는
          // Firestore를 직접 읽어 무관)은 정상 동작했지만, 사용자는 사전 경고 없이
          // 제출 시점에야 거부당하고 있었음.
          isAdultOnly: !!theme.isAdultOnly,
          notices: theme.notices || '',
          refundPolicy: theme.refundPolicy || '',
          pricing: theme.pricing || [],
          price: theme.pricing?.length > 0
            ? Math.min(...theme.pricing.map(p => Number(p.price) || 0))
            : null,
          availableTimes: theme.availableTimes || [],
          scheduleType: theme.scheduleType || 'auto',
          // 78차 57차: 시작시간은 테마 자체 값 우선(방마다 첫 회차가 다를 수 있음),
          // 없으면 매장 공통값. 종료시간은 매장 공통.
          // 78차: normalizeTimeStr — 엑셀 시간서식 오염(소수값)이 남아있어도 방어(dateUtils.js 참고)
          startTime: normalizeTimeStr(theme.startTime) || normalizeTimeStr(store.startTime) || '09:00',
          endTime: normalizeTimeStr(theme.endTime) || normalizeTimeStr(store.endTime) || '22:00',
          prepMinutes: theme.prepMinutes ?? 0,
          // 간격 기본값 = 플레이시간(duration) + 준비시간(prepMinutes)
          intervalMinutes: theme.intervalMinutes || (Number(theme.duration || 60) + Number(theme.prepMinutes || 0)),
          blockedSlots: theme.blockedSlots || [],
          blockedDates: theme.blockedDates || [],   // ← 7차: 예약 불가 날짜
          // 78차 38차: 60차에 StoreAdminScreen이 이미 theme.blockedWeekdays/reservationWindowDays를
          // 정상 저장하고 있었는데, 이 flatten 함수가 두 필드를 아예 안 옮기고 있어서
          // ThemeDetailScreen/BookingDetailScreen의 getNext30Days가 항상 빈 배열/null만
          // 받아 반복요일 차단·예약 오픈 기간 설정이 실제로는 전혀 적용되지 않던 버그
          // (blockedSlots 기반 시간대 차단은 저장 시점에 availableTimes에 이미 반영돼
          // 있어서 정상 동작 중이었음 — 이 두 필드만 빠져 있었음).
          blockedWeekdays: theme.blockedWeekdays || [],
          // 78차 48차: 공휴일 자동 차단 — 명시적으로 꺼둔(false) 게 아니면 기본 ON
          blockHolidays: theme.blockHolidays !== false,
          // 78차 62차: 공휴일 자동 차단 중에서도 개별로 예외 처리(정상 영업)한 날짜
          holidayOverrides: theme.holidayOverrides || [],
          reservationWindowDays: theme.reservationWindowDays ?? null,
          // 78차: 예약 오픈 기간이 자정이 아니라 특정 시각에 갱신되는 매장을 위함
          // (예: "오늘로부터 7일 후 22시부터 다음 예약 가능")
          reservationWindowOpenTime: theme.reservationWindowOpenTime ?? null,
          isTemporary: theme.isTemporary || false,
          // 78차 48차: 총관리자 매장 등록/일괄등록 화면은 reservationUrl을 매장(store)
          // 단위 한 필드로만 받는데(한 매장이 외부 예약 시스템 하나를 쓰는 게 보통이라),
          // 실제 소비처(ThemeDetailScreen/BookingDetailScreen의 예약 완료 후 리다이렉트)는
          // theme.reservationUrl만 읽고 있어서 총관리자가 등록한 URL이 조용히 무시되고
          // 있었음(매장관리자 쪽 매장설정엔 테마별 개별 입력란이 있어 그건 정상 동작).
          // 테마별 값이 있으면 그걸 우선하고, 없으면 매장 공통 URL로 폴백.
          reservationUrl: theme.reservationUrl || store.reservationUrl || '',

          // 매장/지점 필드
          storeName: store.ownerName || '',
          branch: branch.branchName || '',
          branchName: branch.branchName || '',
          city: branch.city || '',
          district: branch.district || '',
          // 78차 48차: 상세주소(branch.addressDetail)가 아예 안 옮겨지고 있어서 화면에
          // "서울 광진"까지만 표시되고 지도 검색도 그 수준으로만 되던 버그 — 실제 저장된
          // 상세주소를 그대로 전달.
          addressDetail: branch.addressDetail || '',
          region: branch.city || '',
          subRegion: branch.district || '',
          storeEmail: store.email || '',
          storeContact: store.contact || '',
          // 72차: 매장 일시 휴업 — isDeleted(총괄 관리자 전용 완전 비활성화)와 달리
          // 매장관리자가 스스로 켜고 끌 수 있는 "일시 중단" 상태. 목록에서 숨기지
          // 않고 배지로 표시 + 예약만 막는다(고객이 재방문했을 때 매장이 통째로
          // 사라진 것처럼 보이지 않도록).
          isPaused: store.isPaused === true,
          isDemo: store.isDemo === true,           // 74차: 영업용 데모 매장
          visibleToUid: store.visibleToUid || null, // 74차: 데모 매장을 볼 수 있는 전용 계정 uid

          // 통계 (없으면 기본값)
          rating: theme.rating || 0,
          reviewCount: theme.reviewCount || 0,
          viewCount: theme.viewCount || 0,   // 55차: 조회수
          createdAt: store.createdAt || '',
        });
      });
    });
  }
  return products;
};

// ─── 전체 stores 조회 후 평탄화 ─────────────────────────────
// 32차: isDeleted: true 매장 자동 제외
// 78차 37차: getAllStores()가 홈/예약탭 등 화면 진입마다 매번 stores 컬렉션 전체를
// 다시 스캔하고 있어(별도 products 컬렉션이 없어 이 전체 스캔이 모든 상품 목록의
// 유일한 소스임) "가끔 로딩이 1~2초씩 걸린다"는 신고의 실제 원인이었음 — 짧은(12초)
// 인메모리 캐시 추가. 매장 문서를 직접 쓰는 화면들(StoreAdminScreen/SuperAdminStoresScreen/
// SuperAdminScreen/StoreInfoScreen)은 저장 직후 invalidateStoreCache()를 호출해 반영 지연을
// 없앰 — 12초는 "탭 전환/재방문 시 재요청"만 없애는 수준으로 짧게 잡아 그 외 화면은
// 자연히 몇 초 이상 걸리는 네비게이션 특성상 stale 위험이 낮음.
let _rawStoresCache = { data: null, ts: 0 };
const STORES_CACHE_TTL = 12000;

const getRawStores = async () => {
  const now = Date.now();
  if (_rawStoresCache.data && (now - _rawStoresCache.ts) < STORES_CACHE_TTL) {
    return _rawStoresCache.data;
  }
  const snap = await getDocs(collection(db, 'stores'));
  const stores = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  _rawStoresCache = { data: stores, ts: now };
  return stores;
};

export const invalidateStoreCache = () => { _rawStoresCache = { data: null, ts: 0 }; };

export const getAllStores = async () => {
  const stores = await getRawStores();
  return stores.filter(s => s.isDeleted !== true);
};

// 32차: 삭제된 매장 포함 전체 조회 (SuperAdmin 전용)
export const getAllStoresIncludeDeleted = async () => {
  return getRawStores();
};

// 74차: viewer로 {uid, managedStoreIds}를 넘기면 데모 매장 노출 여부를 같이 걸러냄.
// 안 넘기면(기본값) 데모 매장은 전부 숨김 — 총괄관리자 화면처럼 별도 조회가 필요한 곳은
// getAllStoresIncludeDeleted처럼 이 필터를 안 거치는 함수를 계속 쓰면 됨.
// 80차 후속: [정책 변경] 데모 매장 노출을 "전용 계정만"에서 "완전 공개"로 재전환 —
// 사용자 확인 후 결정. 데모 매장/테마도 일반 매장과 동일하게 비회원 포함 누구에게나
// 노출(isDemo 필드 자체는 관리 화면의 "🧪 데모" 배지 등 표시용으로 그대로 유지, 고객
// 노출 필터링 용도로는 더 이상 쓰지 않음) — visibleToUid 기반 게이트 제거.
const isVisibleToViewer = (p, viewer) => {
  // 80차 후속: 확인된 미성년자(viewer.isMinor)에게는 19금 테마를 목록에서 숨김 —
  // 비회원/생년월일 미등록(isMinor 미지정)은 계속 노출(호출부의 isConfirmedMinorBirth
  // 참고, "모르면 숨기지 않음"이 의도된 기본값).
  if (p.isAdultOnly && viewer?.isMinor) return false;
  return true;
};

export const getAllProducts = async (viewer = null) => {
  const stores = await getAllStores();
  const products = flattenStoreThemes(stores).filter(p => isVisibleToViewer(p, viewer));
  return products.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
};

// ─── 기간한정 테마 ──────────────────────────────────────────
export const getTemporaryProducts = async (viewer = null) => {
  const all = await getAllProducts(viewer);
  return all.filter(p => p.isTemporary === true);
};

// ─── 단건 조회 ("storeId_bi_ti" 형식 id) ────────────────────
// id 형식: "{storeId}_{branchIdx}_{themeIdx}"
// storeId 자체에 '_'가 없다고 가정할 수 없으므로 뒤에서 파싱
export const getProductById = async (id, viewer = null) => {
  const parsed = parseProductId(id);
  if (!parsed) return null;
  const { storeId, branchIdx: bi, themeIdx: ti } = parsed;

  try {
    const snap = await getDoc(doc(db, 'stores', storeId));
    if (!snap.exists()) return null;
    const store = { id: snap.id, ...snap.data() };
    // 32차: 삭제된 매장의 상품은 null 반환
    if (store.isDeleted === true) return null;
    // 80차 후속: [정책 변경] 데모 매장 전용계정 게이트 제거 — 완전 공개로 전환(사용자 확인).
    const products = flattenStoreThemes([store]);
    // 정확한 id 매칭 또는 bi/ti 인덱스 직접 접근
    const byId = products.find(p => p.id === id);
    if (byId) return byId;
    // fallback: 인덱스로 직접 접근
    const branch = store.branches?.[bi];
    const theme  = branch?.themes?.[ti];
    if (!theme) return null;
    return flattenStoreThemes([store]).find(
      p => p.branchIdx === bi && p.themeIdx === ti
    ) || null;
  } catch {
    return null;
  }
};

// ─── 55차: 테마 조회수 증가 ──────────────────────────────────
// id: "storeId_branchIdx_themeIdx"
// stores/{storeId}.branches[bi].themes[ti].viewCount 를 increment(1)
// 실패 시 무음 처리 — 조회수 누락이 앱 동작에 영향 없어야 함
// 78차: 예전엔 클라이언트가 stores 문서의 branches 전체를 읽어 직접 updateDoc으로
// 덮어썼음 — firestore.rules가 이걸 허용하려고 "branches만 바뀌면 로그인한 아무나"
// 예외를 열어뒀는데, 그 예외가 실제로는 다른 매장의 테마 전체(가격/설명/이미지 등)를
// 아무나 마음대로 덮어쓸 수 있는 구멍이었음(전수조사 중 발견) — Admin SDK 전용 CF로
// 이관하고 그 rules 예외를 제거.
export const incrementViewCount = async (id) => {
  try {
    const cf = httpsCallable(functions, 'incrementViewCountCF');
    await cf({ productId: id });
  } catch {
    // 조회수 기록 실패는 무음 처리
  }
};

// ─── 매장 표시용 브랜드명 ─────────────────────────────────────
// store.ownerName이 정본 브랜드명 필드 (store.name은 존재한 적 없는 필드).
// 71차: eventService.js/StoreDetailScreen.js/StoreAdminScreen.js/RecruitWriteScreen.js가
//   각자 만들던 "ownerName 없으면 첫 지점명으로" 폴백 로직을 하나로 통합
export const getStoreDisplayName = (store, fallback = '') =>
  store?.ownerName || store?.branches?.[0]?.branchName || fallback;

// ─── 앱 호환 normalizeProduct ────────────────────────────────
// flattenStoreThemes 결과는 이미 정규화되어 있으나
// 기존 코드 호환을 위해 유지
export const normalizeProduct = (p) => ({
  ...p,
  name: p.name || p.title || '',
  storeName: p.storeName || p.branch || '',
  thumbnailUrl: p.thumbnailUrl || p.imageUrl || '',
  genres: p.genres?.length ? p.genres : (p.genre ? [p.genre] : []),
  price: p.price != null ? p.price
    : p.pricing?.length > 0
      ? Math.min(...p.pricing.map(pr => Number(pr.price) || 0))
      : null,
  blockedDates: p.blockedDates || [],   // ← 명시적으로 유지 (spread만으로는 누락 방지)
  viewCount: p.viewCount || 0,          // 55차: 조회수 정규화
});

// ─── 32차: 매장 soft-delete ──────────────────────────────────
// isDeleted: true 로 플래그 — getAllStores / getAllProducts에서 자동 제외
// deletedAt: ISO 날짜 기록
export const softDeleteStore = async (storeId) => {
  await updateDoc(doc(db, 'stores', storeId), {
    isDeleted: true,
    deletedAt: new Date().toISOString(),
  });
  invalidateStoreCache();
};

// ─── 32차: 매장 복구 ─────────────────────────────────────────
export const restoreStore = async (storeId) => {
  await updateDoc(doc(db, 'stores', storeId), {
    isDeleted: false,
    deletedAt: null,
  });
  invalidateStoreCache();
};

// ─── 75차: 매장 계약 변경 이력 기록 ───────────────────────────
// plan/수수료/계약기간 등 stores 문서 필드가 straight overwrite뿐이라 변경 이력이
// 전혀 남지 않던 문제 — 변경할 때마다 stores/{storeId}/contractHistory에 스냅샷 기록
// 78차 62차: branchIdx — plan/계약/수수료가 지점별로 달라질 수 있어, 이 변경이
// 어느 지점에 대한 것인지 남긴다(null이면 매장 전체/단일지점 레거시 항목).
export const logContractChange = async (storeId, changeType, before, after, changedBy = null, branchIdx = null) => {
  await addDoc(collection(db, 'stores', storeId, 'contractHistory'), {
    changeType, before, after, changedBy, branchIdx,
    changedAt: serverTimestamp(),
  });
};

export const getContractHistory = async (storeId) => {
  const snap = await getDocs(collection(db, 'stores', storeId, 'contractHistory'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.changedAt?.toMillis?.() || 0) - (a.changedAt?.toMillis?.() || 0));
};